import React, { useState, useEffect } from "react";
import Button from "../../Components/Button";
import { supabase } from "../../supabaseClient";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const CastMemberForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [castName, setCastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [originalCastName, setOriginalCastName] = useState("");


  useEffect(() => {
    if (id) {
      setLoading(true);
      supabase
        .from("castMembers")
        .select("castMembers")
        .eq("id", id)
        .single()
        .then(({ data, error }) => {
          if (error || !data) {

            Swal.fire({
              position: "top-end",
              icon: "error",
              title: "Failed to load cast member",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              toast: true,
              background: "white",
              customClass: {
                popup: "shadow-lg rounded-md p-3",
              },
            });
            navigate("/cast-members");
          } else {
            setCastName(data.castMembers);
            setOriginalCastName(data.castMembers.trim().toLowerCase());

          }
          setLoading(false);
        });
    }
  }, [id, navigate]);

  const handleSave = async (e) => {
    e.preventDefault();

    if (!castName.trim()) {
      Swal.fire({
        position: "top-end",
        icon: "warning",
        title: "Please enter a cast member name",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        toast: true,
        background: "white",
        customClass: {
          popup: "shadow-lg rounded-md p-3",
        },
      });
      return;
    }
    setLoading(true);
    const normalized = castName.trim().toLowerCase();

    if (!id) {
      const { data: existing } = await supabase
        .from("castMembers")
        .select("id")
        .eq("castMembers", castName.trim());

      if (existing && existing.length > 0) {
        setLoading(false);
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: `"${castName}" already exists`,
          showConfirmButton: false,
          timer: 3000,
          toast: true,
          background: "white",
          customClass: { popup: "shadow-lg rounded-md p-3" },
        });
        return;
      }
    }

    if (id) {
      if (normalized !== originalCastName) {
        const { data: existing } = await supabase
          .from("castMembers")
          .select("id")
          .eq("castMembers", castName.trim())
          .neq("id", id);

        if (existing && existing.length > 0) {
          setLoading(false);
          Swal.fire({
            position: "top-end",
            icon: "error",
            title: `"${castName}" already exists`,
            showConfirmButton: false,
            timer: 3000,
            toast: true,
            background: "white",
            customClass: { popup: "shadow-lg rounded-md p-3" },
          });
          return;
        }
      }
    }


    if (id) {
      const { error } = await supabase
        .from("castMembers")
        .update({ castMembers: castName.trim() })
        .eq("id", id);
      setLoading(false);

      if (error) {
        Swal.fire({
          position: "top-end",
          icon: "error",
          title: "Failed to update cast member",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          toast: true,
          background: "white",
          customClass: {
            popup: "shadow-lg rounded-md p-3",
          },
        });
      } else {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Cast member updated successfully",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          toast: true,
          background: "white",
          customClass: {
            popup: "shadow-lg rounded-md p-3",
          },
        });
        navigate("/cast-members");
      }
    } else {
      const { error } = await supabase
        .from("castMembers")
        .insert([{ castMembers: castName.trim() }]);
      setLoading(false);

      if (error) {
        Swal.fire({
          position: "top-end", // top-right corner
          icon: "error", // success, error, warning, info
          title: "Failed to add cast member", // your message
          showConfirmButton: false, // no OK button
          timer: 3000, // auto close after 3 seconds
          timerProgressBar: true, // shows a progress bar
          toast: true, // makes it a toast
          background: "white", // optional, Tailwind prefers light bg
          customClass: {
            popup: "shadow-lg rounded-md p-3", // Tailwind classes
          },
        });
      } else {
        // alert("Cast member added successfully");
        Swal.fire({
          position: "top-end", // top-right corner
          icon: "success", // success, error, warning, info
          title: "Cast member added successfully", // your message
          showConfirmButton: false, // no OK button
          timer: 3000, // auto close after 3 seconds
          timerProgressBar: true, // shows a progress bar
          toast: true, // makes it a toast
          background: "white", // optional, Tailwind prefers light bg
          customClass: {
            popup: "shadow-lg rounded-md p-3", // Tailwind classes
          },
        });
        navigate("/cast-members");
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col p-6 bg-gray-50">
      <div className="mb-6">
        <Button
          variant="secondary"
          className="flex items-center gap-2 px-4 py-2"
          onClick={() => navigate(-1)}
        >
          <span className="text-lg">←</span> Back
        </Button>
      </div>

      <div className="flex-grow flex items-center justify-center">
        <form
          onSubmit={handleSave}
          className="space-y-6 max-w-md w-full bg-white border border-gray-300 rounded-md p-8 shadow-md"
        >
          <h1 className="text-3xl font-bold text-center text-gray-800">
            {id ? "Edit Cast Member" : "Add Cast Member"}
          </h1>

          <div>
            <label
              htmlFor="castName"
              className="block mb-2 font-semibold text-gray-700"
            >
              Enter cast member name
            </label>
            <input
              id="castName"
              name="castName"
              type="text"
              value={castName}
              onChange={(e) => setCastName(e.target.value)}
              placeholder="Enter cast member name"
              autoFocus
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>

          <div className="flex justify-center gap-4">
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
              className="px-8 py-3 font-semibold text-lg"
            >
              {loading ? "Saving..." : id ? "Update" : "Save"}
            </Button>

            <Button
              variant="secondary"
              onClick={() => navigate("/cast-members")}
              className="px-8 py-3 font-semibold text-lg"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CastMemberForm;
